/**
 * HOZ Cookie Consent Manager
 * PDPA-compliant cookie consent for GA4, Meta Pixel, TikTok Pixel
 *
 * Premium UX Features:
 * - Floating card design with spring animation
 * - Two-button hierarchy (Manage Preferences + Accept All)
 * - Focus trap in modal for accessibility
 * - Screen reader announcements
 * - Reduced motion support
 *
 * Cookie Categories:
 * - essential: Always allowed (cart, session, security)
 * - analytics: Google Analytics 4
 * - marketing: Meta Pixel, TikTok Pixel
 *
 * Storage: localStorage key 'hoz_cookie_consent'
 *
 * Shopify Integration:
 * - Integrates with Shopify Customer Privacy API to prevent duplicate banners
 * - Sets tracking consent via Shopify.customerPrivacy.setTrackingConsent()
 */

/* global gtag, fbq, dataLayer, Shopify */

(function () {
  'use strict';

  const STORAGE_KEY = 'hoz_cookie_consent';
  const CONSENT_VERSION = '1.2'; // Bumped for Shopify Customer Privacy integration

  // Default consent state (all off until user opts in - PDPA compliant)
  const defaultConsent = {
    version: CONSENT_VERSION,
    essential: true, // Always true
    analytics: false,
    marketing: false,
    timestamp: null,
  };

  // DOM element cache
  let elements = {
    banner: null,
    modalWrapper: null,
    modal: null,
    backdrop: null,
    statusRegion: null,
    previouslyFocused: null,
  };

  /**
   * Get stored consent or return null if not set
   */
  function getStoredConsent() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const consent = JSON.parse(stored);

      // Check if consent version matches
      if (consent.version !== CONSENT_VERSION) {
        return null; // Re-ask for consent on version change
      }

      return consent;
    } catch {
      return null;
    }
  }

  /**
   * Save consent to localStorage
   */
  function saveConsent(consent) {
    consent.timestamp = new Date().toISOString();
    consent.version = CONSENT_VERSION;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  }

  /**
   * Apply consent settings to tracking scripts and Shopify Customer Privacy API
   */
  function applyConsent(consent) {
    // Google Analytics 4 consent mode
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
      });
    }

    // Meta Pixel consent
    if (typeof fbq === 'function') {
      if (consent.marketing) {
        fbq('consent', 'grant');
      } else {
        fbq('consent', 'revoke');
      }
    }

    // TikTok Pixel - limited consent API, we control loading instead
    // If marketing is denied, TikTok pixel should not be loaded

    // Shopify Customer Privacy API - sync our consent with Shopify's system
    // This prevents Shopify from showing its native banner
    syncShopifyConsent(consent);

    // Dispatch event for any custom integrations
    window.dispatchEvent(
      new CustomEvent('hoz:cookie-consent', {
        detail: consent,
      })
    );
  }

  /**
   * Sync consent with Shopify Customer Privacy API
   * This tells Shopify that the user has interacted with a consent banner,
   * preventing Shopify's native banner from appearing.
   */
  function syncShopifyConsent(consent) {
    // Use Shopify's loadFeatures to ensure Customer Privacy API is available
    if (typeof Shopify !== 'undefined' && Shopify.loadFeatures) {
      Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        function (error) {
          if (error) {
            console.warn('HOZ Cookie Consent: Could not load Shopify consent-tracking-api');
            return;
          }

          // Set tracking consent via Shopify's API
          // This marks the user as having made a consent decision
          if (Shopify.customerPrivacy && typeof Shopify.customerPrivacy.setTrackingConsent === 'function') {
            const trackingConsent = consent.analytics || consent.marketing;
            Shopify.customerPrivacy.setTrackingConsent(
              trackingConsent,
              function () {
                // Consent synced with Shopify successfully
              }
            );
          }
        }
      );
    }
  }

  /**
   * Announce message to screen readers
   */
  function announce(message) {
    if (elements.statusRegion) {
      elements.statusRegion.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        elements.statusRegion.textContent = '';
      }, 1000);
    }
  }

  /**
   * Show the consent banner
   */
  function showBanner() {
    if (elements.banner) {
      // Use requestAnimationFrame to ensure CSS transition works
      requestAnimationFrame(() => {
        elements.banner.classList.add('is-visible');
      });
    }
  }

  /**
   * Hide the consent banner
   */
  function hideBanner() {
    if (elements.banner) {
      elements.banner.classList.remove('is-visible');
    }
  }

  /**
   * Get focusable elements within a container
   */
  function getFocusableElements(container) {
    return container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
  }

  /**
   * Trap focus within modal
   */
  function handleModalKeydown(e) {
    if (e.key === 'Escape') {
      hideModal();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements(elements.modal);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Show the customization modal
   */
  function showModal() {
    if (!elements.modalWrapper || !elements.modal || !elements.backdrop) return;

    // Store currently focused element
    elements.previouslyFocused = document.activeElement;

    // Show wrapper
    elements.modalWrapper.classList.remove('hidden');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Trigger animations
    requestAnimationFrame(() => {
      elements.backdrop.classList.add('is-visible');
      elements.modal.classList.add('is-visible');
    });

    // Set toggle states based on current consent or defaults
    const consent = getStoredConsent() || { analytics: true, marketing: true };
    const analyticsToggle = elements.modal.querySelector('[data-cookie-toggle="analytics"]');
    const marketingToggle = elements.modal.querySelector('[data-cookie-toggle="marketing"]');

    if (analyticsToggle) analyticsToggle.checked = consent.analytics !== false;
    if (marketingToggle) marketingToggle.checked = consent.marketing !== false;

    // Add focus trap
    elements.modal.addEventListener('keydown', handleModalKeydown);

    // Focus first focusable element
    setTimeout(() => {
      const focusableElements = getFocusableElements(elements.modal);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }, 100);

    // Announce to screen readers
    announce('Cookie preferences dialog opened');
  }

  /**
   * Hide the customization modal
   */
  function hideModal() {
    if (!elements.modalWrapper || !elements.modal || !elements.backdrop) return;

    // Remove focus trap
    elements.modal.removeEventListener('keydown', handleModalKeydown);

    // Hide with animation
    elements.backdrop.classList.remove('is-visible');
    elements.modal.classList.remove('is-visible');

    // Wait for animation then hide wrapper
    setTimeout(() => {
      elements.modalWrapper.classList.add('hidden');
      document.body.style.overflow = '';

      // Restore focus
      if (elements.previouslyFocused && elements.previouslyFocused.focus) {
        elements.previouslyFocused.focus();
      }
    }, 300);
  }

  /**
   * Handle accept all
   */
  function acceptAll() {
    const consent = {
      ...defaultConsent,
      analytics: true,
      marketing: true,
    };
    saveConsent(consent);
    applyConsent(consent);
    hideBanner();
    hideModal();
    announce('All cookies accepted. Preferences saved.');
  }

  /**
   * Handle reject all
   */
  function rejectAll() {
    const consent = {
      ...defaultConsent,
      analytics: false,
      marketing: false,
    };
    saveConsent(consent);
    applyConsent(consent);
    hideBanner();
    hideModal();
    announce('Optional cookies rejected. Preferences saved.');
  }

  /**
   * Handle save preferences from modal
   */
  function savePreferences() {
    const analyticsToggle = elements.modal?.querySelector('[data-cookie-toggle="analytics"]');
    const marketingToggle = elements.modal?.querySelector('[data-cookie-toggle="marketing"]');

    const consent = {
      ...defaultConsent,
      analytics: analyticsToggle?.checked ?? false,
      marketing: marketingToggle?.checked ?? false,
    };

    saveConsent(consent);
    applyConsent(consent);
    hideBanner();
    hideModal();

    // Build descriptive announcement
    const enabledTypes = [];
    if (consent.analytics) enabledTypes.push('analytics');
    if (consent.marketing) enabledTypes.push('marketing');

    if (enabledTypes.length === 0) {
      announce('Only essential cookies enabled. Preferences saved.');
    } else {
      announce(`${enabledTypes.join(' and ')} cookies enabled. Preferences saved.`);
    }
  }

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    // Button actions via event delegation
    document.addEventListener('click', (e) => {
      const actionElement = e.target.closest('[data-cookie-action]');
      if (!actionElement) return;

      const action = actionElement.dataset.cookieAction;

      switch (action) {
        case 'accept':
          acceptAll();
          break;
        case 'reject':
        case 'reject-modal':
          rejectAll();
          break;
        case 'customize':
          showModal();
          break;
        case 'close-modal':
          hideModal();
          break;
        case 'save-preferences':
          savePreferences();
          break;
      }
    });

    // Close modal on backdrop click
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cookie-modal-backdrop]')) {
        hideModal();
      }
    });
  }

  /**
   * Cache DOM elements
   */
  function cacheElements() {
    elements.banner = document.querySelector('[data-cookie-consent-banner]');
    elements.modalWrapper = document.querySelector('[data-cookie-modal-wrapper]');
    elements.modal = document.querySelector('[data-cookie-modal]');
    elements.backdrop = document.querySelector('[data-cookie-modal-backdrop]');
    elements.statusRegion = document.getElementById('cookie-consent-status');
  }

  /**
   * Initialize Google Analytics consent mode defaults
   * This should run before GA loads
   */
  function initGoogleConsentMode() {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Set default consent to denied (PDPA compliant)
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
  }

  /**
   * Check if Shopify's Customer Privacy API indicates consent already given
   * Returns a Promise that resolves to true if user already consented via Shopify
   */
  function checkShopifyConsentState() {
    return new Promise((resolve) => {
      if (typeof Shopify === 'undefined' || !Shopify.loadFeatures) {
        resolve(false);
        return;
      }

      Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        function (error) {
          if (error || !Shopify.customerPrivacy) {
            resolve(false);
            return;
          }

          // Check if user has already interacted with consent
          const trackingConsent = Shopify.customerPrivacy.getTrackingConsent();
          // 'no_interaction' means user hasn't made a choice yet
          // Any other value means they've already interacted
          resolve(trackingConsent !== 'no_interaction');
        }
      );
    });
  }

  /**
   * Main initialization
   */
  function init() {
    // Initialize Google consent mode defaults first
    initGoogleConsentMode();

    // Cache DOM elements
    cacheElements();

    // Set up event listeners
    initEventListeners();

    // Check for existing consent in our storage
    const existingConsent = getStoredConsent();

    if (existingConsent) {
      // User has already consented via our banner, apply their preferences
      applyConsent(existingConsent);
    } else {
      // Check if Shopify already has consent (e.g., from a previous session)
      // If so, don't show our banner either
      checkShopifyConsentState().then((hasShopifyConsent) => {
        if (hasShopifyConsent) {
          // Shopify already has consent, create a default consent record
          // to prevent our banner from showing
          const defaultAcceptConsent = {
            ...defaultConsent,
            analytics: true,
            marketing: true,
          };
          saveConsent(defaultAcceptConsent);
          applyConsent(defaultAcceptConsent);
        } else {
          // No consent yet, show banner after a short delay
          // Slightly longer delay for premium feel - let page load first
          setTimeout(showBanner, 1500);
        }
      });
    }
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
